import {
  JoonUIColor,
  Modal,
  SegmentedControl,
  Typography,
} from "@joonapp/web-shared"
import { useEffect, useState } from "react"
import { isAndroid, isMobile } from "react-device-detect"
import { Trans, useTranslation } from "react-i18next"
import { create } from "zustand"

import { PlatformType, SubscribedStatus } from "../../types"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { getStoredCountry } from "../../util/country"

interface DownloadMobileAppModalStore {
  modalType: SubscribedStatus
  isOpen: boolean
  onOpen: (modalType?: SubscribedStatus) => void
  onClose: () => void
}

export const useDownloadMobileAppModal = create<DownloadMobileAppModalStore>(
  (set) => ({
    modalType: SubscribedStatus.NOT_SUBSCRIBED,
    isOpen: false,
    onOpen: (modalType = SubscribedStatus.NOT_SUBSCRIBED) => {
      set({ modalType })
      set({ isOpen: true })
      trackAnalyticEvent(ANALYTIC_EVENTS.MOBILE_APP_DOWNLOAD_PROMPT_SEEN)
    },
    onClose: () => set({ isOpen: false }),
  })
)

const DownloadMobileAppModal = () => {
  const isFranceAppleBlocked = getStoredCountry()?.toUpperCase() === "FR"
  const [platform, setPlatform] = useState<PlatformType>(PlatformType.IOS)
  const { isOpen, onClose, modalType } = useDownloadMobileAppModal()
  const { t } = useTranslation()
  const isIOS = platform === PlatformType.IOS
  const deviceLabel = isIOS
    ? t("DOWNLOAD_APP_DEVICE_IOS")
    : t("DOWNLOAD_APP_DEVICE_ANDROID")
  const storeLabel = isIOS
    ? t("DOWNLOAD_APP_STORE_IOS")
    : t("DOWNLOAD_APP_STORE_ANDROID")
  const storeUrl = isIOS
    ? "https://apps.apple.com/us/app/joon-behavior-improvement-app/id1482225056"
    : "https://play.google.com/store/apps/details?id=com.joon.android"

  useEffect(() => {
    if (isAndroid || isFranceAppleBlocked) {
      setPlatform(PlatformType.ANDROID)
    }
  }, [isFranceAppleBlocked])

  return (
    <Modal isOpen={isOpen} onClose={onClose} displayCloseIcon animate>
      <div
        className="flex flex-col gap-6 p-6 items-center"
        style={{ width: "min(400px, 95vw)" }}
      >
        <div className="flex flex-col gap-4">
          {modalType === SubscribedStatus.SUBSCRIBED ? (
            <>
              <Typography variant="h4" textAlign="center">
                {t("DOWNLOAD_APP_TRIAL_ACTIVE")}
              </Typography>
              <Typography variant="bodySmall" textAlign="center">
                {t("DOWNLOAD_APP_TRIAL_ACTIVE_DESC")}
              </Typography>
            </>
          ) : (
            <Typography variant="h4" textAlign="center">
              {t("DOWNLOAD_APP_GET_MOST")}
            </Typography>
          )}

          {!isFranceAppleBlocked && (
            <SegmentedControl
              value={platform}
              setValue={(value) => setPlatform(value as PlatformType)}
              options={[
                {
                  label: t("DOWNLOAD_APP_IOS"),
                  value: PlatformType.IOS,
                },
                {
                  label: t("DOWNLOAD_APP_ANDROID"),
                  value: PlatformType.ANDROID,
                },
              ]}
              fullWidth
            />
          )}
        </div>
        <div
          className={`flex gap-4 p-4 bg-bgXLightGray rounded-md items-center ${isMobile ? "flex-col-reverse" : "flex-col"}`}
        >
          <div className="flex flex-col gap-4 items-center">
            <Typography
              variant={isMobile ? "bodySmall" : "body"}
              textAlign="center"
            >
              {isMobile
                ? t("DOWNLOAD_APP_SCAN_MOBILE", { device: deviceLabel })
                : t("DOWNLOAD_APP_SCAN", { device: deviceLabel })}
            </Typography>
            <img
              src={isIOS ? "/images/qr-ios.png" : "/images/qr-android.png"}
              alt={isIOS ? "ios-qr-code" : "android-qr-code"}
              style={{ width: "138px" }}
            />
          </div>
          <Typography
            variant={isMobile ? "body" : "bodySmall"}
            className="text-center"
          >
            <Trans
              i18nKey={
                isMobile ? "DOWNLOAD_APP_CLICK_MOBILE" : "DOWNLOAD_APP_CLICK"
              }
              values={{ store: storeLabel }}
              components={{
                link: (
                  <a
                    href={storeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: JoonUIColor.text.primaryAccent,
                      fontWeight: 700,
                      textDecoration: "none",
                    }}
                  >
                    here
                  </a>
                ),
              }}
            />
          </Typography>
        </div>
      </div>
    </Modal>
  )
}

export default DownloadMobileAppModal
